import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Send,
  Minimize2,
  Maximize2,
  Search,
  Plus,
  Users,
  Mail,
  Trash2,
  MailOpen,
  Paperclip,
  File,
  ChevronDown,
  ChevronUp,
  UserCheck,
} from "lucide-react";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { useWebSocket } from "../contexts/WebSocketContext";
import { formatDate } from "../utils/dateUtils";

interface MessageAttachment {
  filename: string;
  originalName: string;
  fileUrl: string;
  mimeType: string;
  fileSize: number;
}

interface Message {
  _id: string;
  senderId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
  };
  content: string;
  attachments?: MessageAttachment[];
  createdAt: string;
  read: boolean;
}

interface Conversation {
  id: string;
  isGroup?: boolean;
  name?: string;
  participantCount?: number;
  participants?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
  }>;
  participant?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
  };
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount: number;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
  type: "staff" | "client" | "subcontractor";
}

interface Group {
  _id: string;
  name: string;
  description?: string;
  color?: string;
  type: "group";
  memberCount: number;
  staffIds: string[];
}

interface Contact {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  type: "client" | "supplier" | "contact";
  hasUserAccount: boolean;
  userId?: string | null;
  contactPersonEmail?: string;
}

interface MessengerWindowProps {
  windowId: string;
  initialConversationId?: string | null;
  initialUserId?: string | null;
  position: { x: number; y: number };
  onClose: (windowId: string) => void;
  onMinimize: (windowId: string) => void;
  onFocus: (windowId: string) => void;
  isMinimized: boolean;
  minimizedIndex?: number;
  zIndex: number;
}

export const MessengerWindow: React.FC<MessengerWindowProps> = ({
  windowId,
  initialConversationId,
  initialUserId,
  position,
  onClose,
  onMinimize,
  onFocus,
  isMinimized,
  minimizedIndex,
  zIndex,
}) => {
  const { t, isRTL } = useTranslation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<
    string | null
  >(initialConversationId || null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [availableStaff, setAvailableStaff] = useState<User[]>([]);
  const [availablePeople, setAvailablePeople] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    staff: true,
    groups: true,
    people: true,
    users: true,
    contacts: true,
  });
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [windowPosition, setWindowPosition] = useState(position);

  // Update position when prop changes
  useEffect(() => {
    setWindowPosition(position);
  }, [position]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<
    string | null
  >(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [typingUsers, setTypingUsers] = useState<
    Map<string, { userId: string; userName: string }>
  >(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);
  const currentUserNameRef = useRef<string>("");

  const { socket } = useWebSocket();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const token = localStorage.getItem("token");

  // Get current user ID from token
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          setCurrentUserId(data.user.id);
          currentUserNameRef.current = `${data.user.firstName} ${data.user.lastName}`;
        }
      } catch (error) {
        console.error("Failed to fetch current user:", error);
      }
    };
    fetchCurrentUser();
  }, [apiUrl, token]);

  // Join/leave conversation room and set up typing listeners
  useEffect(() => {
    if (!socket || !selectedConversation) return;

    // Join conversation room
    socket.emit("conversation:join", selectedConversation);

    // Listen for typing events
    const handleTypingStart = (data: {
      conversationId: string;
      userId: string;
      userName: string;
    }) => {
      if (
        data.conversationId === selectedConversation &&
        data.userId !== currentUserId
      ) {
        setTypingUsers((prev) => {
          const newMap = new Map(prev);
          newMap.set(data.userId, {
            userId: data.userId,
            userName: data.userName,
          });
          return newMap;
        });

        // Auto-clear typing indicator after 3 seconds
        setTimeout(() => {
          setTypingUsers((prev) => {
            const newMap = new Map(prev);
            newMap.delete(data.userId);
            return newMap;
          });
        }, 3000);
      }
    };

    const handleTypingStop = (data: {
      conversationId: string;
      userId: string;
    }) => {
      if (
        data.conversationId === selectedConversation &&
        data.userId !== currentUserId
      ) {
        setTypingUsers((prev) => {
          const newMap = new Map(prev);
          newMap.delete(data.userId);
          return newMap;
        });
      }
    };

    // Listen for new messages in real-time (only for selected conversation)
    const handleNewMessage = (data: {
      conversationId: string;
      message: Message;
      timestamp: string;
    }) => {
      // Only process messages for the currently selected conversation
      if (data.conversationId === selectedConversation) {
        // Add new message to the messages list
        setMessages((prev) => {
          // Check if message already exists (prevent duplicates)
          const exists = prev.some((m) => m._id === data.message._id);
          if (exists) {
            return prev;
          }
          return [...prev, data.message];
        });

        // If the message is from another user and conversation is open, mark as read
        if (data.message.senderId._id !== currentUserId) {
          fetch(
            `${apiUrl}/api/messages/conversations/${selectedConversation}/read`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          ).catch((err) =>
            console.error("Failed to mark message as read:", err),
          );
        }
      }
      // Note: Conversation list refresh is handled by global listener
    };

    socket.on("typing:start", handleTypingStart);
    socket.on("typing:stop", handleTypingStop);
    socket.on("message:new", handleNewMessage);

    return () => {
      socket.emit("conversation:leave", selectedConversation);
      socket.off("typing:start", handleTypingStart);
      socket.off("typing:stop", handleTypingStop);
      socket.off("message:new", handleNewMessage);
      // Clear typing indicator when leaving
      setTypingUsers(new Map());
      // Stop sending typing events
      if (isTypingRef.current && socket) {
        socket.emit("typing:stop", {
          conversationId: selectedConversation,
          userId: currentUserId,
        });
        isTypingRef.current = false;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }
    };
  }, [socket, selectedConversation, currentUserId, apiUrl, token]);

  // Fetch conversations
  const fetchConversations = async () => {
    try {
      setLoadingConversations(true);
      const response = await fetch(`${apiUrl}/api/messages/conversations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setConversations(data.data);
        // Calculate total unread messages and notify parent
        const totalUnread = data.data.reduce(
          (sum: number, conv: Conversation) => sum + (conv.unreadCount || 0),
          0,
        );
        window.dispatchEvent(
          new CustomEvent("unreadMessagesUpdate", { detail: totalUnread }),
        );
        // If we have initialConversationId, select it
        if (initialConversationId && !selectedConversation) {
          setSelectedConversation(initialConversationId);
        }
      }
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Fetch messages for a conversation
  const fetchMessages = async (conversationId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiUrl}/api/messages/conversations/${conversationId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();
      if (data.success) {
        setMessages(data.data);
        // Mark messages as read
        await fetch(
          `${apiUrl}/api/messages/conversations/${conversationId}/read`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        // Refresh conversations to update unread count
        await fetchConversations();
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available users, groups, and contacts for new conversations
  const fetchAvailableUsers = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/messages/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setAvailableStaff(data.data.staff || []);
        setAvailablePeople(data.data.people || []);
        setAvailableUsers(data.data.users || []);
        setAvailableGroups(data.data.groups || []);
        setAvailableContacts(data.data.contacts || []);
        console.log("Fetched staff:", data.data.staff?.length || 0);
        console.log("Fetched people:", data.data.people?.length || 0);
        console.log("Fetched users:", data.data.users?.length || 0);
        console.log(
          "Fetched contacts:",
          data.data.contacts?.length || 0,
          data.data.contacts,
        );
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  // Handle file selection with validation
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Allowed file types (matching backend validation)
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
        "text/plain",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/zip",
        "application/x-zip-compressed",
      ];

      // Allowed file extensions (fallback)
      const allowedExtensions = [
        ".pdf",
        ".doc",
        ".docx",
        ".txt",
        ".xls",
        ".xlsx",
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".webp",
        ".zip",
      ];

      const maxFileSize = 50 * 1024 * 1024; // 50MB (matching backend)

      const validFiles: File[] = [];
      const errors: string[] = [];

      files.forEach((file) => {
        // Check file type
        const isValidType =
          allowedTypes.includes(file.type) ||
          allowedExtensions.some((ext) =>
            file.name.toLowerCase().endsWith(ext),
          );

        if (!isValidType) {
          errors.push(
            `${file.name}: File type not allowed. Allowed types: PDF, DOC, DOCX, TXT, XLS, XLSX, images (JPG, PNG, GIF, WEBP), ZIP`,
          );
          return;
        }

        // Check file size
        if (file.size > maxFileSize) {
          errors.push(
            `${file.name}: File size exceeds 50MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`,
          );
          return;
        }

        validFiles.push(file);
      });

      // Show errors if any
      if (errors.length > 0) {
        alert(`Some files were rejected:\n\n${errors.join("\n")}`);
      }

      // Add valid files
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }

      // Reset input to allow selecting the same file again
      e.target.value = "";
    }
  };

  // Handle paste event for images
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData.items;
    const imageFiles: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.indexOf("image") !== -1) {
        const blob = item.getAsFile();
        if (blob) {
          // Create a File object with a proper name
          const timestamp = Date.now();
          const extension = blob.type.split("/")[1] || "png";
          const fileName = `pasted-image-${timestamp}.${extension}`;
          // Convert Blob to File - File constructor is available in modern browsers
          let imageFile: File;
          if (blob instanceof File) {
            // If already a File, create a new one with the new name
            imageFile = new (File as any)([blob], fileName, {
              type: blob.type,
              lastModified: blob.lastModified,
            });
          } else {
            // Convert Blob to File
            imageFile = new (File as any)([blob], fileName, {
              type: blob.type,
              lastModified: Date.now(),
            });
          }
          imageFiles.push(imageFile);
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      setSelectedFiles((prev) => [...prev, ...imageFiles]);
    }
  };

  // Remove selected file
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev[index];
      // Clean up object URL if it's an image
      if (fileToRemove && fileToRemove.type.startsWith("image/")) {
        const url = URL.createObjectURL(fileToRemove);
        URL.revokeObjectURL(url);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [selectedFiles]);

  // Upload files and get attachment data
  const uploadFiles = async (files: File[]): Promise<MessageAttachment[]> => {
    if (files.length === 0) return [];

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await fetch(`${apiUrl}/api/messages/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    if (data.success) {
      return data.data;
    }
    throw new Error("Failed to upload files");
  };

  // Send message
  const handleSendMessage = async () => {
    if (
      (!inputValue.trim() && selectedFiles.length === 0) ||
      !selectedConversation
    )
      return;

    const conversation = conversations.find(
      (c) => c.id === selectedConversation,
    );
    if (!conversation) return;

    try {
      setSendingMessage(true);
      setUploadingFiles(true);
      let attachments: MessageAttachment[] = [];

      // Upload files if any
      if (selectedFiles.length > 0) {
        attachments = await uploadFiles(selectedFiles);
      }

      const requestBody: any = {
        content: inputValue.trim() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      };

      // For group conversations, use conversationId; for 1-on-1, use recipientId
      if (conversation.isGroup) {
        requestBody.conversationId = selectedConversation;
      } else {
        requestBody.recipientId = conversation.participant?.id;
      }

      const response = await fetch(`${apiUrl}/api/messages/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.success) {
        setInputValue("");
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        // Stop typing indicator when message is sent
        if (socket && selectedConversation && isTypingRef.current) {
          socket.emit("typing:stop", {
            conversationId: selectedConversation,
            userId: currentUserId,
          });
          isTypingRef.current = false;
        }
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        await fetchMessages(selectedConversation);
        await fetchConversations();
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      alert(
        t("messaging.sendFailed") ||
          "Failed to send message. Please try again.",
      );
    } finally {
      setSendingMessage(false);
      setUploadingFiles(false);
    }
  };

  // Start new conversation
  const handleStartConversation = async () => {
    try {
      let requestBody: any = {};

      if (selectedGroup) {
        // Start group conversation from staff group
        requestBody.groupId = selectedGroup;
      } else if (selectedUsers.length > 0) {
        // Start group conversation with selected users
        requestBody.recipientIds = selectedUsers;
      } else {
        return; // No selection
      }

      // Create or get conversation (without sending a message)
      const response = await fetch(`${apiUrl}/api/messages/conversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.success) {
        setShowNewConversation(false);
        setSelectedConversation(data.data.id);
        setInputValue(""); // Empty input, ready for user to type
        setSelectedUsers([]);
        setSelectedGroup(null);
        await fetchMessages(data.data.id);
        await fetchConversations();
      }
    } catch (error) {
      console.error("Failed to start conversation:", error);
    }
  };

  // Toggle user selection for group conversation
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
    setSelectedGroup(null); // Clear group selection when selecting users
  };

  // Select group for conversation
  const handleGroupSelection = (groupId: string) => {
    setSelectedGroup(groupId);
    setSelectedUsers([]); // Clear user selection when selecting group
  };

  // Get user type badge color
  const getUserTypeBadge = (type: string) => {
    switch (type) {
      case "client":
        return (
          <span className="text-xs px-2 py-0.5 rounded bg-accent-100 dark:bg-accent-900 text-accent-700 dark:text-accent-300">
            {t("messaging.client")}
          </span>
        );
      case "supplier":
        return (
          <span className="text-xs px-2 py-0.5 rounded bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300">
            Supplier
          </span>
        );
      case "contact":
        return (
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
            Contact
          </span>
        );
      case "subcontractor":
        return (
          <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300">
            {t("messaging.subcontractor")}
          </span>
        );
      case "staff":
      default:
        return (
          <span className="text-xs px-2 py-0.5 rounded bg-success-100 dark:bg-success-900 text-success-700 dark:text-success-300">
            {t("messaging.staff")}
          </span>
        );
    }
  };

  // Filter users, groups, and contacts based on search
  const filteredStaff =
    userSearchQuery.trim() === ""
      ? availableStaff
      : availableStaff.filter(
          (person) =>
            `${person.firstName} ${person.lastName}`
              .toLowerCase()
              .includes(userSearchQuery.toLowerCase()) ||
            person.email.toLowerCase().includes(userSearchQuery.toLowerCase()),
        );

  const filteredPeople =
    userSearchQuery.trim() === ""
      ? availablePeople
      : availablePeople.filter(
          (person) =>
            `${person.firstName} ${person.lastName}`
              .toLowerCase()
              .includes(userSearchQuery.toLowerCase()) ||
            person.email.toLowerCase().includes(userSearchQuery.toLowerCase()),
        );

  const filteredUsers =
    userSearchQuery.trim() === ""
      ? availableUsers
      : availableUsers.filter(
          (person) =>
            `${person.firstName} ${person.lastName}`
              .toLowerCase()
              .includes(userSearchQuery.toLowerCase()) ||
            person.email.toLowerCase().includes(userSearchQuery.toLowerCase()),
        );

  const filteredGroups =
    userSearchQuery.trim() === ""
      ? availableGroups
      : availableGroups.filter(
          (group) =>
            group.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
            (group.description &&
              group.description
                .toLowerCase()
                .includes(userSearchQuery.toLowerCase())),
        );

  const filteredContacts =
    userSearchQuery.trim() === ""
      ? availableContacts
      : availableContacts.filter(
          (contact) =>
            contact.name
              .toLowerCase()
              .includes(userSearchQuery.toLowerCase()) ||
            (contact.email &&
              contact.email
                .toLowerCase()
                .includes(userSearchQuery.toLowerCase())),
        );

  // Toggle section expansion
  const toggleSection = (
    section: "staff" | "groups" | "people" | "users" | "contacts",
  ) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      // Stop typing indicator when sending
      if (socket && selectedConversation && isTypingRef.current) {
        socket.emit("typing:stop", {
          conversationId: selectedConversation,
          userId: currentUserId,
        });
        isTypingRef.current = false;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    }
  };

  // Handle typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    if (!socket || !selectedConversation) return;

    // Clear existing debounce timer
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
    }

    // If user is typing and we haven't sent typing:start yet
    if (e.target.value.trim() && !isTypingRef.current) {
      socket.emit("typing:start", {
        conversationId: selectedConversation,
        userId: currentUserId,
        userName: currentUserNameRef.current,
      });
      isTypingRef.current = true;
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator after 2 seconds of inactivity
    typingDebounceRef.current = setTimeout(() => {
      if (socket && selectedConversation && isTypingRef.current) {
        socket.emit("typing:stop", {
          conversationId: selectedConversation,
          userId: currentUserId,
        });
        isTypingRef.current = false;
      }
    }, 2000);
  };

  // Mark messages as unread
  const handleMarkAsUnread = async (conversationId: string) => {
    try {
      const response = await fetch(
        `${apiUrl}/api/messages/conversations/${conversationId}/unread`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        // Refresh conversations to update unread count
        await fetchConversations();
      } else {
        alert(
          data.message ||
            t("messaging.markUnreadFailed") ||
            "Failed to mark messages as unread",
        );
      }
    } catch (error) {
      console.error("Failed to mark messages as unread:", error);
      alert(
        t("messaging.markUnreadFailed") || "Failed to mark messages as unread",
      );
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (
    conversationId: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setConversationToDelete(conversationId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!conversationToDelete) return;

    try {
      const response = await fetch(
        `${apiUrl}/api/messages/conversations/${conversationToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        setConversations((prev) =>
          prev.filter((c) => c.id !== conversationToDelete),
        );
        if (selectedConversation === conversationToDelete) {
          setSelectedConversation(null);
          setMessages([]);
        }
        await fetchConversations();
        // Close window if this was the only conversation
        if (conversations.length <= 1) {
          onClose(windowId);
        }
      } else {
        alert(data.message || "Failed to delete conversation");
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
      alert("Failed to delete conversation");
    } finally {
      setShowDeleteConfirm(false);
      setConversationToDelete(null);
    }
  };

  // Initial load
  useEffect(() => {
    fetchConversations();
    fetchAvailableUsers();
  }, []);

  // If initialUserId is provided, start conversation with that user (backward compatibility)
  useEffect(() => {
    if (initialUserId && !selectedConversation && availablePeople.length > 0) {
      // For backward compatibility, create single-user conversation
      fetch(`${apiUrl}/api/messages/conversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          recipientId: initialUserId,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setSelectedConversation(data.data.id);
            setInputValue("");
            fetchMessages(data.data.id);
            fetchConversations();
          }
        })
        .catch((error) => {
          console.error("Failed to start conversation:", error);
        });
    }
  }, [initialUserId, selectedConversation, availablePeople.length]);

  // Global listener for new messages (even when no conversation is selected)
  // This ensures conversation list updates in real-time
  useEffect(() => {
    if (!socket || isMinimized) return;

    const handleNewMessage = (data: {
      conversationId: string;
      message: Message;
      timestamp: string;
    }) => {
      // Update conversation list locally (no API call needed!)
      // We'll update unread count and last message from the message data
      setConversations((prev) => {
        const updated = prev.map((conv) => {
          if (conv.id === data.conversationId) {
            // If message is from another user, increment unread count
            const isFromOtherUser = data.message.senderId._id !== currentUserId;
            return {
              ...conv,
              lastMessage: data.message.content.substring(0, 50),
              lastMessageAt: data.timestamp,
              unreadCount:
                isFromOtherUser && conv.id !== selectedConversation
                  ? (conv.unreadCount || 0) + 1
                  : conv.unreadCount, // Don't increment if viewing this conversation
            };
          }
          return conv;
        });

        // Dispatch unread count update event for RightSidebar
        const totalUnread = updated.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0,
        );
        window.dispatchEvent(
          new CustomEvent("unreadMessagesUpdate", { detail: totalUnread }),
        );

        return updated;
      });

      // If this message is for the currently selected conversation, add it to messages
      if (selectedConversation === data.conversationId && !isMinimized) {
        setMessages((prev) => {
          const exists = prev.some((m) => m._id === data.message._id);
          if (exists) {
            return prev;
          }
          return [...prev, data.message];
        });
      }
    };

    const handleConversationUpdate = (data: {
      conversationId: string;
      update: any;
      timestamp: string;
    }) => {
      // Update conversation in the list
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === data.conversationId) {
            return {
              ...conv,
              lastMessage: data.update.lastMessage,
              lastMessageAt: data.update.lastMessageAt,
            };
          }
          return conv;
        }),
      );
    };

    socket.on("message:new", handleNewMessage);
    socket.on("conversation:update", handleConversationUpdate);

    return () => {
      socket.off("message:new", handleNewMessage);
      socket.off("conversation:update", handleConversationUpdate);
    };
  }, [socket, selectedConversation, isMinimized, currentUserId]);

  // Load messages when conversation is selected
  useEffect(() => {
    if (selectedConversation && !isMinimized) {
      fetchMessages(selectedConversation);
    } else if (!selectedConversation) {
      // Clear messages when no conversation is selected
      setMessages([]);
    }
  }, [selectedConversation, isMinimized]);

  // No polling needed - real-time updates via Socket.io handle message delivery
  // Messages are delivered instantly via 'message:new' events
  // If Socket.io connection fails, the user will see a disconnect indicator
  // and can manually refresh if needed

  // Auto scroll to bottom when messages change
  useEffect(() => {
    if (!isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isMinimized]);

  const filteredConversations =
    searchQuery.trim() === ""
      ? conversations
      : conversations.filter((conv) => {
          if (conv.isGroup) {
            return (
              conv.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              false
            );
          } else {
            return (
              conv.participant &&
              (`${conv.participant.firstName} ${conv.participant.lastName}`
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
                conv.participant.email
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()))
            );
          }
        });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("common.justNow");
    if (diffMins < 60) return `${diffMins}${t("common.minutesAgo")}`;
    if (diffHours < 24) return `${diffHours}${t("common.hoursAgo")}`;
    if (diffDays < 7) return `${diffDays}${t("common.daysAgo")}`;
    return formatDate(date);
  };

  if (isMinimized) {
    const conversation = conversations.find(
      (c) => c.id === selectedConversation,
    );
    const minimizedRight =
      minimizedIndex !== undefined ? 20 + minimizedIndex * 290 : 20;
    return (
      <div
        ref={windowRef}
        className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-t-lg shadow-lg cursor-pointer"
        style={{
          bottom: 0,
          right: `${minimizedRight}px`,
          width: "280px",
          zIndex: zIndex,
          left: "auto", // Ensure left is not set when minimized
        }}
        onClick={() => onFocus(windowId)}
      >
        <div
          ref={headerRef}
          className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {conversation?.isGroup ? (
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                <Users className="w-4 h-4" />
              </div>
            ) : conversation?.participant?.profileImage ? (
              <img
                src={conversation.participant.profileImage}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                {conversation
                  ? getInitials(
                      conversation.participant?.firstName || "",
                      conversation.participant?.lastName || "",
                    )
                  : "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {conversation
                  ? conversation.isGroup
                    ? conversation.name ||
                      `${conversation.participantCount || 0} ${t("messaging.participants")}`
                    : `${conversation.participant?.firstName || ""} ${conversation.participant?.lastName || ""}`
                  : t("messaging.messages")}
              </p>
              {conversation && conversation.unreadCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {conversation.unreadCount} {t("messaging.new")}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMinimize(windowId);
              }}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label={t("messaging.maximize")}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose(windowId);
              }}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label={t("common.close")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={windowRef}
      className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-t-lg shadow-xl flex flex-col"
      style={{
        left: `${windowPosition.x}px`,
        top: `${windowPosition.y}px`,
        width: isMinimized ? "280px" : "320px",
        height: isMinimized ? "60px" : "500px",
        zIndex: zIndex,
      }}
      onClick={() => onFocus(windowId)}
    >
      {/* Header */}
      <div
        ref={headerRef}
        className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700"
      >
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          {t("messaging.messages")}
        </h3>
        <div className="flex items-center space-x-2">
          {!selectedConversation && !showNewConversation && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowNewConversation(true);
              }}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label={t("messaging.newConversation")}
            >
              <Plus className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMinimize(windowId);
            }}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label={t("messaging.minimize")}
          >
            <Minimize2 className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose(windowId);
            }}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {showNewConversation ? (
        <>
          {/* New Conversation Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setShowNewConversation(false);
                setSelectedUsers([]);
                setSelectedGroup(null);
                setUserSearchQuery("");
              }}
              className="text-primary-600 dark:text-primary-400 text-sm hover:underline mb-2"
            >
              ← {t("common.back")}
            </button>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              {t("messaging.newConversation")}
            </h4>
            {(selectedUsers.length > 0 || selectedGroup) && (
              <button
                onClick={handleStartConversation}
                className="mt-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm transition-colors"
              >
                {t("messaging.startConversation")}{" "}
                {selectedUsers.length > 0 && `(${selectedUsers.length + 1})`}
              </button>
            )}
          </div>

          {/* Search */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                placeholder="Search users, groups, or contacts..."
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Available Groups List */}
          {filteredGroups.length > 0 && (
            <div className="border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => toggleSection("groups")}
                className="w-full p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("messaging.groups")} ({filteredGroups.length})
                </h5>
                {expandedSections.groups ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {expandedSections.groups && (
                <div className="px-3 pb-3">
                  {filteredGroups.map((group) => (
                    <button
                      key={group._id}
                      onClick={() => handleGroupSelection(group._id)}
                      className={`w-full p-3 rounded-lg mb-2 text-left transition-colors ${
                        selectedGroup === group._id
                          ? "bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500"
                          : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                          style={{ backgroundColor: group.color || "#6366f1" }}
                        >
                          <Users className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                            {group.name}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {group.memberCount}{" "}
                            {group.memberCount === 1
                              ? t("messaging.member")
                              : t("messaging.members")}
                          </p>
                        </div>
                        {selectedGroup === group._id && (
                          <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Available Staff List */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection("staff")}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Staff ({filteredStaff.length})
              </h5>
              {expandedSections.staff ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {expandedSections.staff && (
              <div className="px-3 pb-3">
                {filteredStaff.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                    No staff available
                  </p>
                ) : (
                  filteredStaff.map((user) => (
                    <button
                      key={user._id}
                      onClick={() => toggleUserSelection(user._id)}
                      className={`w-full p-3 rounded-lg mb-2 text-left transition-colors ${
                        selectedUsers.includes(user._id)
                          ? "bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span>
                              {getInitials(user.firstName, user.lastName)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                              {user.firstName} {user.lastName}
                            </h4>
                            {getUserTypeBadge(user.type)}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                        </div>
                        {selectedUsers.includes(user._id) && (
                          <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Available People List */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection("people")}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                People ({filteredPeople.length})
              </h5>
              {expandedSections.people ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {expandedSections.people && (
              <div className="px-3 pb-3">
                {filteredPeople.map((user) => (
                  <button
                    key={user._id}
                    onClick={() => toggleUserSelection(user._id)}
                    className={`w-full p-3 rounded-lg mb-2 text-left transition-colors ${
                      selectedUsers.includes(user._id)
                        ? "bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                        {user.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span>
                            {getInitials(user.firstName, user.lastName)}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                            {user.firstName} {user.lastName}
                          </h4>
                          {getUserTypeBadge(user.type)}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                      </div>
                      {selectedUsers.includes(user._id) && (
                        <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Available Users List */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection("users")}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Users ({filteredUsers.length})
              </h5>
              {expandedSections.users ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {expandedSections.users && (
              <div className="px-3 pb-3">
                {filteredUsers.map((user) => (
                  <button
                    key={user._id}
                    onClick={() => toggleUserSelection(user._id)}
                    className={`w-full p-3 rounded-lg mb-2 text-left transition-colors ${
                      selectedUsers.includes(user._id)
                        ? "bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                        {user.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span>
                            {getInitials(user.firstName, user.lastName)}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                            {user.firstName} {user.lastName}
                          </h4>
                          {getUserTypeBadge(user.type)}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                      </div>
                      {selectedUsers.includes(user._id) && (
                        <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Available Contacts List */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection("contacts")}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Contacts ({filteredContacts.length})
              </h5>
              {expandedSections.contacts ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {expandedSections.contacts && (
              <div className="px-3 pb-3">
                {filteredContacts.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                    No contacts available
                  </p>
                ) : (
                  filteredContacts.map((contact) => (
                    <button
                      key={contact._id}
                      onClick={() => {
                        if (contact.hasUserAccount && contact.userId) {
                          toggleUserSelection(contact.userId);
                        } else {
                          alert(
                            "This contact does not have a user account. They need to be invited to the system first.",
                          );
                        }
                      }}
                      disabled={!contact.hasUserAccount}
                      className={`w-full p-3 rounded-lg mb-2 text-left transition-colors ${
                        contact.hasUserAccount &&
                        contact.userId &&
                        selectedUsers.includes(contact.userId)
                          ? "bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500"
                          : contact.hasUserAccount
                            ? "hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent"
                            : "opacity-50 cursor-not-allowed border border-transparent"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-accent-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                          <UserCheck className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                              {contact.name}
                            </h4>
                            {getUserTypeBadge(contact.type)}
                            {!contact.hasUserAccount && (
                              <span className="text-xs text-gray-400">
                                (No account)
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {contact.email ||
                              contact.contactPersonEmail ||
                              "No email"}
                          </p>
                        </div>
                        {contact.hasUserAccount &&
                          contact.userId &&
                          selectedUsers.includes(contact.userId) && (
                            <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Empty State */}
          <div className="flex-1 overflow-y-auto">
            {filteredStaff.length === 0 &&
            filteredPeople.length === 0 &&
            filteredUsers.length === 0 &&
            filteredGroups.length === 0 &&
            filteredContacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">{t("messaging.noUsersAvailable")}</p>
              </div>
            ) : null}
          </div>
        </>
      ) : !selectedConversation ? (
        <>
          {/* Search */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("messaging.searchConversations")}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loadingConversations ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-2"></div>
                <p className="text-sm">
                  {t("messaging.loadingConversations") ||
                    "Loading conversations..."}
                </p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <Mail className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">{t("messaging.noConversations")}</p>
                <button
                  onClick={() => setShowNewConversation(true)}
                  className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm transition-colors"
                >
                  {t("messaging.startConversation")}
                </button>
              </div>
            ) : (
              <div>
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    className="group relative w-full border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div
                      onClick={() => {
                        setSelectedConversation(conv.id);
                        fetchMessages(conv.id);
                      }}
                      className="w-full p-4 text-left cursor-pointer"
                    >
                      <div className="flex items-start space-x-3">
                        {conv.isGroup ? (
                          <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                            <Users className="w-5 h-5" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                            {conv.participant?.profileImage ? (
                              <img
                                src={conv.participant.profileImage}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span>
                                {getInitials(
                                  conv.participant?.firstName || "",
                                  conv.participant?.lastName || "",
                                )}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0 pr-8">
                          <div className="flex justify-between items-baseline mb-1">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                              {conv.isGroup
                                ? conv.name ||
                                  `${conv.participantCount || 0} ${t("messaging.participants")}`
                                : `${conv.participant?.firstName || ""} ${conv.participant?.lastName || ""}`}
                            </h4>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatTimestamp(conv.lastMessageAt)}
                              </span>
                              <button
                                onClick={(e) =>
                                  handleDeleteConversation(conv.id, e)
                                }
                                className="p-1 text-gray-400 hover:text-error-600 dark:hover:text-error-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label={t("messaging.deleteConversation")}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {conv.lastMessage || t("messaging.noMessagesYet")}
                            </p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {conv.isGroup && conv.participantCount && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {conv.participantCount}{" "}
                                  {conv.participantCount === 1
                                    ? t("messaging.member")
                                    : t("messaging.members")}
                                </span>
                              )}
                              {conv.unreadCount > 0 && (
                                <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {conv.unreadCount}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Conversation Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              {selectedConversation && (
                <button
                  onClick={() => handleMarkAsUnread(selectedConversation)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                  aria-label={t("messaging.markAsUnread") || "Mark as unread"}
                  title={t("messaging.markAsUnread") || "Mark as unread"}
                >
                  <MailOpen className="w-4 h-4" />
                </button>
              )}
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              {(() => {
                const conv = conversations.find(
                  (c) => c.id === selectedConversation,
                );
                if (conv?.isGroup) {
                  return (
                    conv.name ||
                    `${conv.participantCount || 0} ${t("messaging.participants")}`
                  );
                }
                return `${conv?.participant?.firstName || ""} ${conv?.participant?.lastName || ""}`;
              })()}
            </h4>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {(!currentUserId || (loading && messages.length === 0)) ? (
              <div className="flex justify-center items-center h-full">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {t("messaging.loadingMessages")}
                </p>
              </div>
            ) : (
              <>
                {messages.map((message) => {
                  const isCurrentUser = message.senderId._id === currentUserId;
                  return (
                    <div
                      key={message._id}
                      className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          isCurrentUser
                            ? "bg-primary-500 text-white"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {message.content && (
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.content}
                          </p>
                        )}
                        {message.attachments &&
                          message.attachments.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {message.attachments.map((attachment, idx) => {
                                const isImage =
                                  attachment.mimeType?.startsWith("image/") ||
                                  /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                    attachment.originalName,
                                  );
                                return (
                                  <div key={idx}>
                                    {isImage ? (
                                      <a
                                        href={`${apiUrl}${attachment.fileUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                      >
                                        <img
                                          src={`${apiUrl}${attachment.fileUrl}`}
                                          alt={attachment.originalName}
                                          className="max-w-full max-h-64 rounded object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                        />
                                        <span className="text-xs opacity-70 mt-1 block">
                                          {attachment.originalName} (
                                          {(attachment.fileSize / 1024).toFixed(
                                            1,
                                          )}{" "}
                                          KB)
                                        </span>
                                      </a>
                                    ) : (
                                      <a
                                        href={`${apiUrl}${attachment.fileUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 p-2 rounded ${
                                          isCurrentUser
                                            ? "bg-primary-600 hover:bg-primary-700"
                                            : "bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500"
                                        } transition-colors`}
                                      >
                                        <File className="w-4 h-4 flex-shrink-0" />
                                        <span className="text-sm truncate">
                                          {attachment.originalName}
                                        </span>
                                        <span className="text-xs opacity-70">
                                          (
                                          {(attachment.fileSize / 1024).toFixed(
                                            1,
                                          )}{" "}
                                          KB)
                                        </span>
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        <p className="text-xs opacity-70 mt-1">
                          {formatTimestamp(message.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {/* Sending indicator */}
                {sendingMessage && (
                  <div className="flex justify-end">
                    <div className="max-w-[80%] p-3 rounded-lg bg-primary-500 text-white opacity-70">
                      <p className="text-sm italic">Sending...</p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            {selectedFiles.length > 0 && (
              <div className="mb-2 space-y-2">
                {selectedFiles.map((file, index) => {
                  const isImage = file.type.startsWith("image/");
                  return (
                    <div
                      key={index}
                      className="relative p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
                    >
                      {isImage ? (
                        <div className="flex flex-col gap-2">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="max-w-full max-h-48 rounded object-contain"
                          />
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <span className="flex-1 truncate text-xs">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                            <button
                              onClick={() => handleRemoveFile(index)}
                              className="p-1 text-gray-500 hover:text-error-600 dark:hover:text-error-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <File className="w-4 h-4 text-gray-500" />
                          <span className="flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="p-1 text-gray-500 hover:text-error-600 dark:hover:text-error-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex space-x-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.zip"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label={t("messaging.attachFile") || "Attach file"}
                title={t("messaging.attachFile") || "Attach file"}
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <div className="flex-1 relative">
                <input
                  ref={messageInputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  onPaste={handlePaste}
                  placeholder="Type a message... (Paste images to attach)"
                  maxLength={5000}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {inputValue.length > 0 && (
                  <div className="absolute bottom-1 right-2 text-xs text-gray-400 dark:text-gray-500">
                    <span
                      className={
                        inputValue.length > 4500
                          ? "text-primary-500"
                          : inputValue.length > 4800
                            ? "text-error-500"
                            : ""
                      }
                    >
                      {inputValue.length}/5000
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={handleSendMessage}
                disabled={
                  (!inputValue.trim() && selectedFiles.length === 0) ||
                  uploadingFiles ||
                  sendingMessage ||
                  inputValue.length > 5000
                }
                className="px-3 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                aria-label={t("messaging.send")}
              >
                {uploadingFiles || sendingMessage ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={{ zIndex: zIndex + 1000 }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t("messaging.deleteConversation")}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t("messaging.deleteConversationConfirm")}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setConversationToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors"
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
